/**
 * Map cascade-impact data onto per-node visual state for the
 * constellation renderer.
 *
 * The constellation reads /v1/cascade for the live impact picture
 * + the rail metrics. Each impacted entity carries a headline
 * severity; we map that to a 4-tone visual state:
 *
 *   down     -- the incident origin (highest severity)
 *   degraded -- impacted (any severity, propagated)
 *   at-risk  -- not impacted today but with open findings
 *   stable   -- no findings + no impact
 *
 * Channels aren't first-class finding targets in v2 today; their
 * state is derived by walking the cascade's per-channel journey
 * breadcrumbs IF present, else "stable".
 *
 * Until a deriver writes the BLAST_RADIUS chain at production
 * scale, the constellation's authoritative state lives in
 * /v1/cascade. This module is the seam where cascade impacts
 * become renderer-friendly per-node tags.
 */
import type {
  CascadeImpact,
  CascadeSummary,
  TwinNode,
  TwinNodeKind,
} from "shared/api/types";

export type NodeVisualState = "down" | "degraded" | "at-risk" | "stable";

/** Map (entity_kind, entity_id) tuples from the cascade impacts
 * into a flat dict so the renderer can look up state in O(1). */
export function buildStateMap(
  nodes: TwinNode[],
  cascade: CascadeSummary | null | undefined,
): Map<string, NodeVisualState> {
  const out = new Map<string, NodeVisualState>();
  // Default all to stable; specific tags override below.
  for (const n of nodes) out.set(n.id, baselineFor(n));

  if (!cascade) return out;

  // Identify the "down" set: the most critical impact's entity
  // (the cascade banner already orders impacted by severity).
  const downIds = new Set<string>();
  let firstCritical: CascadeImpact | null = null;
  for (const imp of cascade.impacted) {
    if (imp.headline_finding.severity === "critical" && !firstCritical) {
      firstCritical = imp;
    }
  }
  if (firstCritical) {
    downIds.add(firstCritical.entity_id);
  }

  // Mark every impacted entity as degraded (or down if it's the
  // first critical pick above).
  for (const imp of cascade.impacted) {
    const id = imp.entity_id;
    out.set(id, downIds.has(id) ? "down" : "degraded");
  }
  return out;
}

function baselineFor(n: TwinNode): NodeVisualState {
  return n.open_finding_count > 0 ? "at-risk" : "stable";
}

/** RAG colour for the renderer + legend. Uses Tailwind brand
 * tokens (rag.red / rag.amber / rag.green) seeded in P79.1. */
export const STATE_FILL: Record<NodeVisualState, string> = {
  down: "#E2574A",
  degraded: "#D79A3A",
  // P88.1 + P91: the two faint states were near-invisible on the
  // dark stage (P86.2 lifted the labels but not the orb fills).
  // Bumped alpha so stable/at-risk orbs read without competing
  // with the saturated down/degraded incident tones.
  "at-risk": "rgba(215,154,58,0.68)",
  stable: "rgba(200,162,74,0.62)",
};

export const STATE_STROKE: Record<NodeVisualState, string> = {
  down: "#F08A80",
  degraded: "#E6B868",
  "at-risk": "rgba(215,154,58,0.75)",
  stable: "rgba(200,162,74,0.78)",
};

export const STATE_DOT: Record<NodeVisualState, string> = {
  down: "#E2574A",
  degraded: "#D79A3A",
  "at-risk": "#D79A3A",
  stable: "#5AAE81",
};

/** Per-kind base radius for the node circle. Vendors render
 * slightly larger when `material_flag = true` so the supplier
 * core of the constellation reads as the gravity well it is. */
export function nodeRadius(n: TwinNode): number {
  if (n.kind === "vendor") return n.material_flag ? 13 : 9;
  if (n.kind === "ibs") return 13;
  if (n.kind === "service") return 9;
  return 8;
}

/** Kind label for tooltips + the inspector. Capitalises +
 * spells out "IBS" -> "Important Business Service" to match the
 * handoff's vocabulary. */
export const KIND_LABEL: Record<TwinNodeKind, string> = {
  channel: "Channel",
  ibs: "Important Business Service",
  service: "Service",
  vendor: "Vendor",
};
